/**
 * Landing page generation API client.
 *
 * Backend routes (api/routes/landing_pages.py):
 *   GET  /landing-pages/templates
 *   POST /landing-pages/preview
 *   POST /landing-pages/publish
 *
 * The tool's defining behaviour is REFUSING, and every refusal is a
 * decision only an operator can make: which of three forms is wired to
 * their lead routing, which photo shows the service advertised, whether a
 * page may ship without a phone. This client exists to carry those
 * questions to a screen and the answers back.
 */
import { FEATURE_FLAGS } from '@/config/environment';

import { apiClient } from './apiClient';

// Generation calls an LLM and fetches/re-encodes imagery; 20s is not enough.
const PREVIEW_TIMEOUT_MS = 120_000;

const PAGEGEN_BASE_URL =
  import.meta.env.VITE_PAGEGEN_BASE_URL ||
  'https://pagegen-deepseek-api-production.up.railway.app';

export const dshLaneOn = (): boolean =>
  FEATURE_FLAGS.pagegenDsh && !!PAGEGEN_BASE_URL;

const dshBaseUrlConfig = () =>
  dshLaneOn() ? { baseURL: PAGEGEN_BASE_URL } : {};

/**
 * The page SHAPE, merged server-side from the renderer's own treatment
 * registry rather than stored alongside the template row. The skeleton the
 * picker draws is therefore the shape the renderer will actually build; a
 * hardcoded copy on this side would drift the first time a section moved.
 */
export interface LandingTemplateShape {
  /** Body sections in render order. The hero is implicit and comes first. */
  sections: string[];
  /** 'split' floats the form beside the hero; 'under' puts it below. */
  hero_form_layout: 'split' | 'under';
  hero_uses_image: boolean;
  sticky_call_bar: boolean;
  /** Phone is the primary conversion path — no callable number means held. */
  call_first: boolean;
}

export interface LandingTemplate {
  slug: string;
  name: string;
  description: string;
  /** Null when the row has no registry match; render the name alone. */
  shape?: LandingTemplateShape | null;
}

/**
 * Findings are bucketed by CONSEQUENCE, not by cause — what shipped, not
 * why the check fired. `fatal` blocks; `suppressible` hides an element and
 * the page continues; `data_gaps` need someone to supply a verified value;
 * `warns` are read, not acted on.
 */
export interface LandingQa {
  passed: boolean;
  fatal: string[];
  suppressible: string[];
  data_gaps: string[];
  warns: string[];
  blocks: string[];
  facts: Record<string, unknown>;
}

export interface LandingPreviewResponse {
  /** Id of the stored draft, or null when the save did not happen. ⚠ null is
   *  a REPORTABLE state, not a detail to swallow: a history an operator relies
   *  on cannot fail quietly, because an empty list after a day of work looks
   *  exactly like a day with no work. */
  draft_id?: string | null;
  html: string;
  template: string;
  form_provider: string;
  form_id: string;
  form_source: 'config' | 'detected';
  defaulted: string[];
  qa: LandingQa;
  copy_meta: Record<string, unknown>;
  /**
   * What the generator was permitted to do about imagery, and what it did.
   *
   * ⚠ THE BACKEND HAS ALWAYS RETURNED THIS AND NOTHING COULD READ IT. The
   * field was absent from this interface, so every operator-facing surface
   * was blind to why a page shipped without a picture — including the case
   * where stock and AI both ran, both were rejected by the subject gate, and
   * the only visible outcome was an imageless page.
   *
   * Empty object means the fallback never ran, which is the GOOD case: the
   * client's own photograph was used and nothing sourced was needed.
   */
  media_plan?: LandingMediaPlan;
}

/**
 * One human's recorded answer about one named service.
 *
 * ⚠ STATED, NEVER INFERRED — Viktor's rule. Photographability belongs to the
 * SERVICE on the page and not to the business: an agency renting a podcast
 * studio has an intensely photographable page, a plumber's "Financing
 * Options" page has no depictable subject. Hence the attribution: the
 * question six weeks later is not what the record says but who said it.
 */
export interface ServicePhotographability {
  service_name: string;
  photographable: boolean;
  note: string;
  confirmed_by: string;
  confirmed_at: string;
}

/** Sourced conversion facts. Empty halves are not stored. */
export interface LandingArtFactBlock {
  text?: string;
  rating?: string;
  reviews_count?: string;
  platform?: string;
  captured_at?: string;
  source_url?: string;
  is_24_7?: boolean;
  expires?: string;
  amount?: string;
  currency?: string;
  label?: string;
  confirmed_by?: string;
  confirmed_at?: string;
}

export interface LandingArtFactsPayload {
  proof?: LandingArtFactBlock;
  guarantee?: LandingArtFactBlock;
  hours?: LandingArtFactBlock;
  offer?: LandingArtFactBlock;
  entry_price?: LandingArtFactBlock;
}

export interface LandingArtFacts {
  tenant_id: string;
  facts: LandingArtFactsPayload;
  proof_ready: boolean;
  guarantee_ready: boolean;
  available_now_ready?: boolean;
  offer_ready?: boolean;
  price_ready?: boolean;
}

/** @see LandingPreviewResponse.media_plan */
export interface LandingMediaPlan {
  allowed?: boolean;
  /** allowed | declined | unasked | not_needed */
  state?: string;
  reason?: string;
  /** Which sources this client agreed to: 'stock', 'ai'. */
  sources?: string[];
  photographable?: boolean;
  queries?: string[];
  /** One entry per tier tried, e.g. 'stock:all_rejected', 'ai:used'. */
  tiers_attempted?: string[];
  stock_found?: number;
  ai_found?: number;
  candidates_found?: number;
  /** used | all_rejected_by_the_subject_gate | nothing_found */
  outcome?: string;
  /**
   * The identifying word no candidate carried. For a service named after
   * something unphotographable ("SEO") this can never be satisfied by any
   * photograph, which is the signal to state that the service cannot be
   * photographed rather than to keep sourcing.
   */
  gate_missing?: string;
  used?: {
    tier?: string;
    provider?: string;
    matched_query?: string;
    photographer_name?: string;
    attribution_html?: string;
    source_page_url?: string;
  };
}

/**
 * Operator revision notes keyed by section name. Steers the next generation's
 * wording and emphasis; does not license an unsourced claim — the L4 gate is
 * unchanged. Runs carrying notes are stamped '+fb' server-side so a steered
 * page is never scored against an unaided one.
 */
export type LandingSectionFeedback = Record<string, string>;

/**
 * POST /landing-pages/publish. The WordPress result is NESTED under
 * `published` — it was previously typed as flat `{post_id, url}`, which type
 * -checked fine and read undefined at runtime.
 *
 * The page is created as a noindex DRAFT. A human flips it live in wp-admin,
 * and Core cannot see that moment (Page Builder sheet, row 20).
 */
export interface LandingPublishResponse {
  published: {
    post_id?: number;
    id?: number;
    url?: string;
    link?: string;
    status?: string;
  } | null;
  template: string;
  form: string;
  noindex: boolean;
  qa?: LandingQa;
}

export interface LandingPreviewRequest {
  tenant_id: string;
  template_slug: string;
  service_name: string;
  target_keyword?: string;
  /** The campaign's other bids, chosen by the operator. Distributed across
   *  body copy; the H1 comes from target_keyword alone. Never folded into the
   *  service name and never seen by the LP-020 hero matcher. */
  supporting_keywords?: string[];
  /** STATED fact about the service on this page, never inferred and never
   *  inherited from the tenant. undefined = unstated, and the LP-020 hero
   *  check keeps its strict default. false = this service has no physical
   *  subject (marketing, legal, consulting), so the hero no longer has to
   *  depict a thing that has no depiction — it must still be a real image and
   *  the stock-photo detector still runs. */
  service_photographable?: boolean;
  vertical?: string;
  city?: string;
  state?: string;
  engine?: string;
  /** LC-09: ship without a phone, and say why. Never stored on the tenant. */
  no_phone_reason?: string;
  section_feedback?: LandingSectionFeedback;
  overrides?: Record<string, string>;
  /** The template's body sections in the operator's order. Must be a
   *  REORDERING of that template's own sections — the server refuses a list
   *  that adds or drops one, because the copy slots the gates expect are
   *  derived from that set. Omitted means the template's own order. */
  section_order?: string[];
  run_id?: string;
  pagegen?: boolean;
  artifact_id?: string;
  /** The campaign this page is for, carried so the saved draft can be grouped
   *  under it in history. It does NOT change what is generated — ad context is
   *  fetched separately. A run with no campaign is a first-class case (a
   *  hand-typed service has none) and groups under "Not tied to a campaign". */
  campaign_resource_name?: string;
  /** Its display name AS IT IS NOW. Stored on the draft rather than looked up
   *  later, so a rename or pause in Google Ads cannot retitle history. */
  campaign_name?: string;
  ad_group_resource_name?: string;
}

/**
 * A refusal is not an error to swallow. 422 means the tool declined to
 * guess — no form configured, several forms found, no service on record —
 * and the message names what a human has to decide.
 */
/**
 * A 422 body, as a sentence a person can act on.
 *
 * ⚠ THIS WAS `JSON.stringify(detail)`. FastAPI's validation errors carry the
 * offending INPUT, so a rejected 242-keyword request printed all 242 keywords
 * as a wall of JSON into the panel — the operator had to read a pydantic error
 * to learn that a field was too long, and the actual message was buried in the
 * middle of it. The tool's own refusals are already written as prose; only the
 * framework's needed translating.
 */
export function readableDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const lines = detail
      .map((d) => {
        const e = d as { msg?: string; loc?: unknown[] };
        if (!e?.msg) return null;
        // ["body", "supporting_keywords"] -> "supporting keywords"
        const field = Array.isArray(e.loc)
          ? e.loc.filter((x) => typeof x === 'string' && x !== 'body')
              .join(' ').replace(/_/g, ' ')
          : '';
        return field ? `${field}: ${e.msg}` : e.msg;
      })
      .filter(Boolean);
    if (lines.length) return lines.join('\n');
  }
  const msg = (detail as { message?: string })?.message;
  if (typeof msg === 'string' && msg) return msg;
  // Last resort, and deliberately bounded: an unbounded dump is what this
  // function exists to stop.
  const dumped = JSON.stringify(detail) ?? 'The request was rejected.';
  return dumped.length > 400 ? `${dumped.slice(0, 400)}…` : dumped;
}

export class LandingRefusal extends Error {
  detail: string;

  constructor(detail: string) {
    super(detail);
    this.detail = detail;
    this.name = 'LandingRefusal';
  }
}

export interface LandingSuggestions {
  tenant_id: string;
  services: string[];
  cities: string[];
  suggested_keyword: string;
  /** The 28 verticals the rules act on. Anything else is accepted and takes
   *  generic treatment, so the UI pairs this with an "Other" escape. */
  known_verticals: string[];
  /** Keywords from this client's active keyword-research run, volume-ranked. */
  researched_keywords: Array<{ keyword: string; volume: number; cluster: string }>;
  vertical: string;
  /** Which record fields are empty — named so the UI can say why it has
   *  nothing to offer, rather than showing an empty box. */
  missing: string[];
}

export interface AdGroupOption {
  id: string;
  name: string;
  resource_name: string;
  status: string;
}

export interface CampaignOption extends AdGroupOption {
  channel: string;
}

/** One service a campaign's ad groups derived, with what a person needs to
 *  judge it. Bare names were `Cities Suburbs`, `Kansas City KC`, `SKAG: Local
 *  Movers Near Me (Was General)` — geo buckets and ops jargon offered with no
 *  evidence. Shown beside its ad group, its keyword count and the H1 it would
 *  produce, a junk candidate explains itself and no rule had to classify it. */
export interface ServiceCandidate {
  name: string;
  ad_group: string;
  ad_group_resource_name: string;
  keyword_count: number;
  /** The H1 choosing this candidate should produce. Carried here so picking a
   *  service is a COMPLETE answer — otherwise the operator picks "Residential"
   *  while the headline still says "fargo". */
  target_keyword: string;
}

export interface LandingAdContext {
  /** false when this client has no Google Ads account, no readable
   *  credential, or Google refused us. 28 of 53 active clients have no ads at
   *  all, so this is the NORMAL case, not an error — the typed Service field
   *  stays and the page is built from it exactly as before. */
  available: boolean;
  reason: string;
  campaigns: CampaignOption[];
  ad_groups: AdGroupOption[];
  /** Every keyword the chosen campaign bids on, each carrying the ad group
   *  that bids it — the attribution is what lets the checklist group by ad
   *  group, and what the service derivation joins on. */
  keywords: {
    text: string;
    match_type: string;
    ad_group?: string;
    ad_group_resource_name?: string;
  }[];
  /** Derived from the chosen ad group. Fills the form; never locks it.
   *  Empty when the ad group does not name a service — a brand or competitor
   *  group, or one called "Ad group 1". */
  service_name: string;
  target_keyword: string;
  /** false = we could not derive a name and the operator must type one.
   *  Distinct from an empty string during loading: "cannot derive" is an
   *  answer, and validated against 356 real ad groups it happens 22 times. */
  service_name_derivable: boolean;
  /** The distinct services the campaign's ad groups derived. One entry means
   *  they agree and service_name carries it. Two or more means the campaign
   *  genuinely sells more than one thing, service_name is empty, and the
   *  operator picks — they are NEVER joined into "Fencing, Decking", which is
   *  the defect that made LP-020 demand the union of every service and refuse
   *  photographs correct for one of them. */
  service_name_candidates: ServiceCandidate[];
  /** Inferred from the campaign's own vocabulary. Empty when nothing matched
   *  or when two verticals tied — both are honest answers, and picking the
   *  alphabetically lucky one is not. */
  vertical: string;
  vertical_candidates: string[];
  /** Which level answered: 'campaigns' before a campaign is chosen,
   *  'campaign' once one is (every ad group pooled), 'ad_group' when the
   *  operator narrowed to a single group to resolve an ambiguity. */
  scope: 'campaigns' | 'campaign' | 'ad_group';
}

export interface TemplateAdvice {
  slug: string;
  name: string;
  score: number;
  /** 'recommended' (at most one), 'usable', or 'blocked' — blocked options are
   *  still returned, because "why can't I use Rapid Response" is a question an
   *  absent option cannot answer. */
  fit: 'recommended' | 'usable' | 'blocked';
  reasons: string[];
}

export interface TemplateAdviceResponse {
  available: boolean;
  reason: string;
  templates: TemplateAdvice[];
}

export interface LandingFormCandidate {
  provider: string;
  provider_label: string;
  form_id: string;
  occurrences: number;
}

export interface LandingFormConfig {
  tenant_id: string;
  /**
   * `auto` and `none` both show an empty to-do list and mean opposite things:
   * one form was found and detection handles it, versus nothing recognised at
   * all. The screen must say which.
   */
  status: 'configured' | 'auto' | 'ambiguous' | 'none';
  configured: {
    provider: string;
    form_id: string;
    note?: string;
    confirmed_by?: string;
    confirmed_at?: string;
  } | null;
  /** null when nothing is recorded; false means the stored form has vanished. */
  configured_still_present: boolean | null;
  candidates: LandingFormCandidate[];
  providers: { key: string; label: string }[];
}

export interface LandingReadinessRow {
  tenant_id: string;
  business_name: string;
  buildable: boolean;
  logo: boolean;
  palette: boolean;
  form: boolean;
  /** False means the form was NOT checked — never render it as a failure. */
  form_checked: boolean;
  ads: boolean;
  form_reason: string;
  /** Why the logo looks the way it does — plated, recoloured, or as supplied. */
  logo_note: string;
  /**
   * "This mark may be a badge rather than the client's logo."
   *
   * ⚠ RECORDED FOR A HUMAN, NEVER ACTED ON AUTOMATICALLY. Shape alone flags 9
   * legitimate logos in 16, so it can only ask for a look — and a request for
   * a look that nothing surfaces is just a row in a table nobody queries.
   */
  logo_review: string;
  logo_review_days: number;
  /** Past the 14-day review window. A flag that cannot come due never gets picked up. */
  logo_review_overdue: boolean;
  ads_reason: string;
  defaulted: string[];
  error: string;
}

export interface LandingReadiness {
  items: LandingReadinessRow[];
  total: number;
  buildable: number;
  forms_checked: number;
  /** "all" for elevated roles, "member" when these are only the caller's. */
  scope: 'all' | 'member';
}

export const landingPagesService = {
  /**
   * What each client is missing before a page can be built.
   *
   * ⚠ `checkForms` reads every client's live website. It takes ~40s across
   * the roster against ~1s without, so the grid loads without it and one
   * client is verified on demand.
   */
  async readiness(
    tenantId?: string,
    checkForms = false,
  ): Promise<LandingReadiness> {
    const { data } = await apiClient.get('/landing-pages/readiness', {
      params: { tenant_id: tenantId, check_forms: checkForms },
      timeout: 120000,
    });
    return data;
  },

  async formConfig(tenantId: string, probe = true): Promise<LandingFormConfig> {
    const { data } = await apiClient.get('/page-builder/form-config', {
      params: { tenant_id: tenantId, probe },
    });
    return data;
  },

  async saveFormConfig(
    tenantId: string,
    body: { provider: string; form_id: string; note?: string; confirmed_by?: string },
  ) {
    const { data } = await apiClient.put('/page-builder/form-config', body, {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  /**
   * Services this client has a photographability answer on file for.
   *
   * ⚠ A LIST, NOT A LOOKUP. Service names drift, so a record stored for
   * "Vinyl Fence Installation" is not found for "Vinyl Fencing" — and a
   * lookup that misses is invisible. Two visible rows is how somebody
   * notices the same service was answered twice under two names.
   */
  async photographability(tenantId: string): Promise<{
    tenant_id: string;
    services: ServicePhotographability[];
  }> {
    const { data } = await apiClient.get('/landing-pages/photographability', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  /**
   * Operator-attributed conversion facts (Google stars, guarantee, hours).
   *
   * ⚠ NOT TWEAKS. These license claims. An unattributed write is refused.
   */
  async artFacts(tenantId: string): Promise<LandingArtFacts> {
    const { data } = await apiClient.get('/landing-pages/art-facts', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async saveArtFacts(
    tenantId: string,
    facts: LandingArtFactsPayload,
    confirmedBy: string,
  ): Promise<LandingArtFacts> {
    const { data } = await apiClient.put('/landing-pages/art-facts', {
      tenant_id: tenantId,
      facts,
      confirmed_by: confirmedBy,
    });
    return data;
  },

  /** Record an answer for one service, or pass null to clear it entirely. */
  async statePhotographability(
    tenantId: string,
    serviceName: string,
    photographable: boolean | null,
    note = '',
  ): Promise<{ stored: boolean; cleared: boolean }> {
    const { data } = await apiClient.put('/landing-pages/photographability', {
      tenant_id: tenantId,
      service_name: serviceName,
      photographable,
      note,
    });
    return data;
  },

  async adviseTemplates(
    tenantId: string,
    serviceName: string,
    vertical: string,
    photographable?: boolean,
  ): Promise<TemplateAdviceResponse> {
    const { data } = await apiClient.get('/landing-pages/suggest-template', {
      params: {
        tenant_id: tenantId,
        service_name: serviceName,
        vertical,
        ...(photographable === undefined
          ? {}
          : { service_photographable: photographable }),
      },
    });
    return data;
  },

  async adContext(
    tenantId: string,
    campaign?: string | null,
    adGroup?: string | null,
  ): Promise<LandingAdContext> {
    const { data } = await apiClient.get('/landing-pages/ad-context', {
      params: {
        tenant_id: tenantId,
        ...(campaign ? { campaign } : {}),
        ...(adGroup ? { ad_group: adGroup } : {}),
      },
    });
    return data;
  },

  async suggestions(tenantId: string): Promise<LandingSuggestions> {
    const { data } = await apiClient.get('/landing-pages/suggestions', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async templates(): Promise<LandingTemplate[]> {
    const { data } = await apiClient.get('/landing-pages/templates');
    return data?.templates ?? data ?? [];
  },

  /** History for one tenant: campaign -> date -> runs, newest first.
   *  Grouped server-side so ordering and the date boundary have one
   *  definition. */
  async listDrafts(
    tenantId: string,
    pageType = 'landing',
    limit = 200
  ): Promise<LandingDraftHistory> {
    // ⚠ /page-builder, not /landing-pages. History belongs to the SECTION:
    // Location Pages and Service Pages will read the same endpoint with a
    // different page_type rather than growing their own.
    const { data } = await apiClient.get('/page-builder/drafts', {
      params: { tenant_id: tenantId, page_type: pageType, limit },
    });
    return data;
  },

  /** Reopen one run, with the HTML exactly as it was generated. */
  async getDraft(tenantId: string, draftId: string): Promise<LandingDraftFull> {
    const { data } = await apiClient.get(
      `/page-builder/drafts/${encodeURIComponent(draftId)}`,
      { params: { tenant_id: tenantId } }
    );
    return data;
  },

  async putDraftHtml(tenantId: string, draftId: string, html: string): Promise<void> {
    await apiClient.put(
      `/page-builder/drafts/${encodeURIComponent(draftId)}/html`,
      { html },
      { params: { tenant_id: tenantId } }
    );
  },

  async preview(req: LandingPreviewRequest): Promise<LandingPreviewResponse> {
    try {
      const { data } = await apiClient.post(
        '/landing-pages/preview',
        dshLaneOn() ? { ...req, pagegen: true } : req,
        { timeout: PREVIEW_TIMEOUT_MS, ...dshBaseUrlConfig() },
      );
      return data;
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })
        .response;
      if (res?.status === 422) {
        throw new LandingRefusal(readableDetail(res.data?.detail));
      }
      throw err;
    }
  },

  /**
   * ⚠ `draft_id` IS NOT OPTIONAL IN PRACTICE. The backend writes the resulting
   * WordPress post id back onto that draft row, which is what lets History
   * show a run as published and what a republish reconciles against. Omit it
   * and the publish still succeeds — silently leaving the tool believing
   * nothing was ever published, which is the exact bug #1870 fixed one layer
   * down.
   */
  async publish(
    req: LandingPreviewRequest & { draft_id?: string | null },
  ): Promise<LandingPublishResponse> {
    try {
      const { data } = await apiClient.post(
        '/landing-pages/publish',
        dshLaneOn() ? { ...req, pagegen: true } : req,
        { timeout: PREVIEW_TIMEOUT_MS, ...dshBaseUrlConfig() },
      );
      return data;
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })
        .response;
      if (res?.status === 422) {
        throw new LandingRefusal(readableDetail(res.data?.detail));
      }
      throw err;
    }
  },
};


/** One stored run, as the history list shows it. No `html`: the list renders
 *  up to 200 rows and each page is ~150KB, which nothing on screen displays. */
export interface LandingDraftSummary {
  id: string;
  tenant_id: string;
  campaign_resource_name: string | null;
  campaign_name: string | null;
  ad_group_resource_name: string | null;
  service_name: string;
  target_keyword: string;
  vertical: string;
  template_slug: string;
  engine: string;
  run_id: string;
  created_by: string;
  /** Full timestamp, ISO. */
  created_at: string;
  /** The date this run belongs to, decided SERVER-SIDE. Splitting client-side
   *  would use the viewer's timezone, so one run would land on different days
   *  for Manila and St Louis and two people would disagree about when it
   *  happened. */
  created_date: string;
  published_post_id: number | null;
  html_bytes: number;
  defaulted: string[];
  qa: Record<string, unknown>;
}

/** A stored run reopened, with the page exactly as it was generated. Not
 *  re-rendered — templates change, so re-rendering would hand back a different
 *  page wearing this run's name. */
export interface LandingDraftFull extends LandingDraftSummary {
  html: string;
  supporting_keywords: string[];
  section_feedback: LandingSectionFeedback;
  city: string;
  state: string;
  service_photographable: boolean | null;
  copy_meta: Record<string, unknown>;
  form_provider: string;
  form_id: string;
  form_source: string;
}

export interface LandingDraftDateGroup {
  date: string;
  runs: LandingDraftSummary[];
}

export interface LandingDraftCampaignGroup {
  campaign_resource_name: string | null;
  campaign_name: string;
  /** True for the "Not tied to a campaign" group. */
  is_unassigned: boolean;
  dates: LandingDraftDateGroup[];
  run_count: number;
}

export interface LandingDraftHistory {
  groups: LandingDraftCampaignGroup[];
  total: number;
  limit: number;
}
